import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import StepIndicator from '../../components/ui/StepIndicator';

const STEPS = ['Receipt Details', 'Items Received'];
const emptyItem = { product: '', itemName: '', quantity: '', unit: 'pcs' };

const InwardForm = () => {
  const navigate = useNavigate();
  const [step, setStep]         = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients]   = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm]         = useState({
    invoiceNumber: '', invoiceDate: '', supplierId: '', clientId: '',
    poNumber: '', invoiceQuantity: '', vehicleNumber: '', docketLRNumber: '', transporterName: '',
    driverName: '', driverPhone: '', challanNumber: '', arrivalDateTime: '',
  });
  const [items, setItems]       = useState([{ ...emptyItem }]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    api.get('/suppliers').then((r) => setSuppliers(r.data.data)).catch(console.error);
    api.get('/clients').then((r) => setClients(r.data.data)).catch(console.error);
  }, []);

  // Load the product catalog for the chosen client (+ global items) and reset rows.
  useEffect(() => {
    if (!form.clientId) { setProducts([]); return; }
    api.get('/products', { params: { client: form.clientId } })
      .then((r) => setProducts(r.data.data))
      .catch(console.error);
  }, [form.clientId]);

  const addItem    = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: value };
    setItems(updated);
  };
  // Pick a product from the catalog -> auto-fills name + unit, links the SKU.
  const selectProduct = (i, productId) => {
    const p = products.find((x) => x._id === productId);
    const updated = [...items];
    updated[i] = {
      ...updated[i],
      product: productId,
      itemName: p ? p.name : '',
      unit: p ? p.unit : updated[i].unit,
    };
    setItems(updated);
  };

  const validateStep0 = () => {
    if (!form.invoiceNumber || !form.invoiceDate || !form.supplierId || !form.clientId) {
      toast.error('Please fill all required receipt details');
      return false;
    }
    return true;
  };
  const validateStep1 = () => {
    if (items.some((i) => !i.itemName || !i.quantity || Number(i.quantity) <= 0)) {
      toast.error('Please fill all item details with a quantity greater than 0');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/inward', {
        ...form,
        items: items.map((i) => ({
          product: i.product || undefined,
          itemName: i.itemName,
          unit: i.unit,
          quantity: Number(i.quantity),
        })),
      });
      toast.success('Inward receipt created successfully!');
      navigate('/inward');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <StepIndicator steps={STEPS} current={step} />

      {/* Step 0 — Receipt details */}
      {step === 0 && (
        <div className="zip-card p-6 space-y-5">
          <h3 className="font-semibold text-zip-text tracking-tight">Receipt Details</h3>

          {/* Supplier and Arrival Date & Time lead the form. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Supplier *</label>
              <select
                className="zip-input"
                value={form.supplierId}
                onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
              >
                <option value="">Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Arrival Date &amp; Time</label>
              <input
                type="datetime-local"
                className="zip-input"
                value={form.arrivalDateTime}
                onChange={(e) => setForm({ ...form, arrivalDateTime: e.target.value })}
              />
              <p className="text-[11px] text-zip-muted/70 mt-1">When the vehicle actually reached the warehouse.</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Entity (Goods Owner) *</label>
            <select
              className="zip-input"
              value={form.clientId}
              onChange={(e) => { setForm({ ...form, clientId: e.target.value }); setItems([{ ...emptyItem }]); }}
            >
              <option value="">Select Entity</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
              ))}
            </select>
            <p className="text-[11px] text-zip-muted/70 mt-1">Whose goods are being received into the warehouse.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Supplier Invoice No. *</label>
              <input
                type="text"
                className="zip-input"
                value={form.invoiceNumber}
                onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
                placeholder="INV-2024-001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Invoice Date *</label>
              <input
                type="date"
                className="zip-input"
                value={form.invoiceDate}
                onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">PO Number</label>
              <input
                type="text"
                className="zip-input"
                value={form.poNumber}
                onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                placeholder="PO-2024-001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Invoice Quantity</label>
              <input
                type="number"
                min="0"
                className="zip-input"
                value={form.invoiceQuantity}
                onChange={(e) => setForm({ ...form, invoiceQuantity: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Vehicle No.</label>
              <input
                type="text"
                className="zip-input"
                value={form.vehicleNumber}
                onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value })}
                placeholder="MH-12-AB-1234"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Docket / LR No.</label>
              <input
                type="text"
                className="zip-input"
                value={form.docketLRNumber}
                onChange={(e) => setForm({ ...form, docketLRNumber: e.target.value })}
                placeholder="LR-001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Transporter Name</label>
              <input
                type="text"
                className="zip-input"
                value={form.transporterName}
                onChange={(e) => setForm({ ...form, transporterName: e.target.value })}
                placeholder="ABC Logistics"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Driver Name</label>
              <input
                type="text"
                className="zip-input"
                value={form.driverName}
                onChange={(e) => setForm({ ...form, driverName: e.target.value })}
                placeholder="Driver's full name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Driver Phone</label>
              <input
                type="text"
                className="zip-input"
                value={form.driverPhone}
                onChange={(e) => setForm({ ...form, driverPhone: e.target.value })}
                placeholder="9876543210"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Challan Number</label>
              <input
                type="text"
                className="zip-input"
                value={form.challanNumber}
                onChange={(e) => setForm({ ...form, challanNumber: e.target.value })}
                placeholder="CH-2024-001"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 1 — Items Received */}
      {step === 1 && (
        <div className="zip-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-zip-text tracking-tight">Items Received</h3>
            <button
              type="button"
              onClick={addItem}
              className="btn-ghost flex items-center gap-1.5 text-xs"
            >
              <Plus size={13} /> Add Item
            </button>
          </div>

          <div className="space-y-3">
            {items.map((item, i) => (
              <div
                key={i}
                className="p-4 rounded-xl border border-zip-border bg-zip-surface transition-all duration-200"
              >
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-zip-muted mb-1">Product *</label>
                    {products.length > 0 ? (
                      <select
                        className="zip-input text-sm"
                        value={item.product}
                        onChange={(e) => selectProduct(i, e.target.value)}
                      >
                        <option value="">Select product…</option>
                        {products.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.partNumber || p.projectName || p.sku} — {p.partDescription || p.name}
                            {p.client ? '' : ' (global)'}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="zip-input text-sm"
                        value={item.itemName}
                        onChange={(e) => updateItem(i, 'itemName', e.target.value)}
                        placeholder="e.g. Cardboard Box"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-zip-muted mb-1">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      className="zip-input text-sm"
                      value={item.quantity}
                      onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zip-muted mb-1">UOM</label>
                    <select
                      className="zip-input text-sm"
                      value={item.unit}
                      onChange={(e) => updateItem(i, 'unit', e.target.value)}
                    >
                      {['pcs', 'kg', 'box', 'roll', 'ltr', 'mtr'].map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="text-zip-muted hover:text-zip-red transition-colors ml-auto"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="flex gap-5 pt-3 border-t border-zip-border text-sm font-mono">
            <span className="text-zip-muted">Total Items: <span className="text-zip-text font-semibold">{items.length}</span></span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        {step > 0 && (
          <button type="button" onClick={() => setStep(step - 1)} className="btn-ghost">
            Back
          </button>
        )}
        {step < 1 ? (
          <button type="button" onClick={handleNext} className="btn-primary">
            Continue
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting…
              </>
            ) : (
              'Submit Receipt'
            )}
          </button>
        )}
        <button type="button" onClick={() => navigate('/inward')} className="btn-ghost ml-auto">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InwardForm;
